import { SubtleButton, theme, Tooltip } from '@arnold/common';
import { ReactComponent as AIIcon } from '@arnold/common/lib/assets/illustrations/AI.svg';
import { ReactComponent as Refresh } from '@arnold/common/src/assets/icons/Refresh.svg';
import styled from '@emotion/styled';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Features, ReportSequenceRespondentDetailQueryQuery } from '../../generated/hooks';
import { IntercomEvent, IntercomEventSender } from '../components/ObserverEventSender';
import RateAi from '../components/RateAi';
import KeyPoint from './KeyPoint';

interface RespondentSummaryProps {
  report: ReportSequenceRespondentDetailQueryQuery['getProcessReportRespondentDetail'];
}

const AiBox = styled.div`
  border: 1px solid ${theme.colors.ai.default};
  padding: 1rem 2rem;
  @media (max-width: ${theme.breakpoint.small}) {
    padding-top: 16px;
    padding-left: 16px;
  }
  //
`;

const AiBoxHeader = styled.div`
  display: flex;
  align-items: center;
`;

const RespondentSummary: React.FC<RespondentSummaryProps> = ({ report }) => {
  const { t } = useTranslation('RespondentSummary');
  if (
    !report.keyPoints ||
    !report.keyPoints.length ||
    !report.metadata.organization.featureNames?.includes(Features.AiSummaryOfRespondent)
  ) {
    return null;
  }

  return (
    <>
      <AiSection title={t('title')} description={t('description')}>
        {report.keyPoints.map((keyPoint) => {
          return <KeyPoint key={keyPoint.point} keyPoint={keyPoint} report={report} />;
        })}
      </AiSection>
      <RateAi type="RESPONDENT_SUMMARY" />
      <IntercomEventSender eventName={IntercomEvent.RespondentSummarySeen} data={{ reportId: report.id }} />
    </>
  );
};

export const AiSection = ({
  title,
  description,
  children,
  className,
  centered,
  showRefreshButton,
  onRefresh,
  refreshTooltip,
}: {
  title: string;
  description: string;
  children?: ReactNode;
  className?: string;
  centered?: boolean;
  showRefreshButton?: boolean;
  onRefresh?: () => void;
  refreshTooltip?: string;
}) => {
  return (
    <div className={className || 'mb-9'}>
      <AiBox className={'bg-white rounded-lg shadow-sm'} data-cy="report-summary-of-respondent">
        {centered ? (
          <AiBoxHeaderCentered>
            <AiBoxHeaderRow>
              <StyledAiIcon />
              <h4 className="mb-0 ml-4">{title}</h4>
            </AiBoxHeaderRow>
            <AiBoxHeaderDescription className={'font-size-xs mb-0 mt-2'}>{description}</AiBoxHeaderDescription>
            <RefreshButton onClick={onRefresh} show={showRefreshButton} tooltip={refreshTooltip} />
          </AiBoxHeaderCentered>
        ) : (
          <AiBoxHeader>
            <StyledAiIcon />
            <div className="ml-6">
              <h4 className="mb-2">{title}</h4>
              <p className={'font-size-xs mb-0'}>{description}</p>
            </div>
            <RefreshButton onClick={onRefresh} show={showRefreshButton} tooltip={refreshTooltip} />
          </AiBoxHeader>
        )}
        {children}
      </AiBox>
    </div>
  );
};

const RefreshButton = ({ onClick, show, tooltip }: { onClick?: () => void; show?: boolean; tooltip?: string }) => {
  if (!show) return null;
  const button = (
    <SubtleButton size="small" leftIcon={<StyledRefresh />} iconScale={1.2} onClick={onClick} className="ml-auto" />
  );
  if (!tooltip) {
    return button;
  }
  return (
    <Tooltip title={tooltip} className="ml-auto">
      {button}
    </Tooltip>
  );
};

export default RespondentSummary;

const StyledRefresh = styled(Refresh)`
  margin-top: -3px;
  margin-left: -1px;
`;

const StyledAiIcon = styled(AIIcon)`
  @media (max-width: ${theme.breakpoint.small}) {
    align-self: flex-start;
  }
`;

const AiBoxHeaderCentered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${theme.spacing.d};
`;

const AiBoxHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AiBoxHeaderDescription = styled.p`
  width: 100%;
`;
